import { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Send, Download, Building2, User, Mail, Phone, MapPin, Hash, ChevronRight, Check, Loader2, ShoppingCart, Plus, Minus, Package, Monitor, Layers, AlertCircle, ScrollText, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const NYC_SCHOOLS_API = "https://data.cityofnewyork.us/resource/wg9x-4ke6.json";

const CATEGORY_META = {
  single_grade: { label: 'Single Grade Packs', icon: Package, color: 'bg-sky-50 border-sky-200', badge: 'bg-sky-100 text-sky-700', desc: '20 student copies + Teacher Edition per grade (all 4 modalities included)' },
  bundle:       { label: 'Print Bundles',       icon: Layers,  color: 'bg-emerald-50 border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', desc: 'Multi-grade print materials — 20 copies + Teacher Edition per grade' },
  digital:      { label: 'Digital Site Licenses', icon: Monitor, color: 'bg-purple-50 border-purple-200', badge: 'bg-purple-100 text-purple-700', desc: 'Per-building school year site license — listening, voice recording, auto-scoring, analytics. Contact us for district/multi-building pricing.' },
  complete:     { label: 'Complete Bundles',     icon: Layers,  color: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700', desc: 'Print + Digital per building — the full MOSAIC experience for your school. Contact us for district/multi-building pricing.' },
  usb_sealed:   { label: 'Sealed MOSAIC USB Edition — Offline Teacher Portal', icon: Package, color: 'bg-slate-50 border-slate-300', badge: 'bg-slate-900 text-amber-300', desc: 'Premium offline USB drive. 100% offline — no cloud, no AI, no NYC ERMA 90-day review required. Includes the full Scoring Portal, Dashboards, Resources, Pacing Guides, Passage Audio, AES-256 encryption and 3-layer self-heal. Shipped in a retail-grade retail box with a serial-numbered drive, Quick Start card, Pre-Insertion Checklist, Thank You letter and User Manual. Per building, 1-year license.' },
  teacher_edition: { label: 'MOSAIC Teacher Editions', icon: Package, color: 'bg-amber-50 border-amber-300', badge: 'bg-amber-900 text-amber-50', desc: 'Grade-level instructional materials for the 2026-2027 school year. One Teacher Edition per grade, priced per teacher edition. NYCPS-eligible single-item purchase under school discretionary materials.' },
  teacher_tools: { label: 'Teacher Guide & Tool Kit', icon: Package, color: 'bg-rose-50 border-rose-200', badge: 'bg-rose-100 text-rose-700', desc: 'Comprehensive teacher resource guide with classroom tools, activity templates, and language-proficiency strategy cards — per grade per class.' },
  toolkit:        { label: 'Teacher Toolkit (Digital Add-On)', icon: Monitor, color: 'bg-sky-50 border-sky-200', badge: 'bg-sky-100 text-sky-700', desc: 'automated teacher tools: Speaking Practice Lab, Vocabulary Builder, Writing Portfolio, Practice Workbooks, Parent Letters, ELL Screener — per building per year.' },
  sses:          { label: 'MOSAIC Student Stability & Engagement — Student Kits (K-8)', icon: Package, color: 'bg-indigo-50 border-indigo-200', badge: 'bg-indigo-100 text-indigo-700', desc: 'Physical (non-digital) per-student Student Kits with one SKU per grade K-8. Includes StoryPath literacy booklets, LifeMath applied-math cards, CalmFocus trauma-informed SEL lessons, Offline Learning Quest gamified workbook, and TalkBridge bilingual family reply sheets (10 languages). Flat $149.00 per student.' },
  entering_level: { label: 'Entering-Level Booklets (K-2)', icon: Layers, color: 'bg-emerald-50 border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', desc: 'Additive K-2 SKU for Entering-Level (Level 1-2) learners. Same scene images, options, and answer keys as the standard booklets — question stems rewritten in 4-8 word Tier-1 vocabulary. Priced with 15% rep commission built in.' },
  screener_print: { label: 'Mosaic Language Proficiency Screener — Print Edition', icon: Package, color: 'bg-blue-50 border-blue-200', badge: 'bg-blue-100 text-blue-700', desc: 'Print edition of the Mosaic Language Proficiency Screener. Teacher Administration Kits + consumable Student Response Booklets. Content is a 1-for-1 companion to the digital screener; no images (text/audio prompts only).' },
  supplemental_print: { label: 'Screener Picture Card Sets', icon: Package, color: 'bg-teal-50 border-teal-200', badge: 'bg-teal-100 text-teal-700', desc: 'Durable cardstock Picture Card Sets that accompany the K Screener and support non-verbal item presentation. Reusable across cohorts.' },
  coordinator:    { label: 'Coordinator Print Kit', icon: Package, color: 'bg-fuchsia-50 border-fuchsia-200', badge: 'bg-fuchsia-100 text-fuchsia-700', desc: '10-month mailed pack for school/district coordinators: rosters, scoring status reports, pacing guides, and compliance tracking. Ships monthly.' },
  custom:        { label: 'Other Products',           icon: Package, color: 'bg-gray-50 border-gray-200', badge: 'bg-gray-100 text-gray-700', desc: 'Additional MOSAIC products and services.' },
};

const QuoteGenerator = () => {
  const [step, setStep] = useState(1); // 1=products, 2=contact, 3=confirm
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({}); // { sku: quantity }
  const [contact, setContact] = useState({
    school_name: '', school_dbn: '', school_address: '',
    contact_name: '', contact_email: '', contact_phone: '',
    it_admin_name: '', it_admin_email: '',
    purchasing_admin_name: '', purchasing_admin_email: '',
  });
  const [notes, setNotes] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(() => new Date().toISOString().slice(0, 10));
  // v6.33 — customer-chosen delivery (default: download only, no auto-email)
  const [deliveryMethod, setDeliveryMethod] = useState('download'); // 'download' | 'email' | 'both'
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [schoolQuery, setSchoolQuery] = useState('');
  const [schoolSuggestions, setSchoolSuggestions] = useState([]);
  const [schoolSearching, setSchoolSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const schoolRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/quotes/products`).then(r => setProducts(r.data.products || []));
  }, []);

  // v6.38.11 — UTM beacon: log inbound QR / social clicks to /api/analytics/utm/hit
  // so the pencil14 dashboard can attribute leads. Fires once per mount; only
  // when at least one utm_* param is present. Silent on failure — analytics
  // must never block a legitimate lead form.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const utm = {
        utm_source:   params.get('utm_source')   || undefined,
        utm_medium:   params.get('utm_medium')   || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
        utm_content:  params.get('utm_content')  || undefined,
        utm_term:     params.get('utm_term')     || undefined,
      };
      if (!utm.utm_source && !utm.utm_campaign) return;
      axios.post(`${API}/api/analytics/utm/hit`, {
        path: window.location.pathname,
        referrer: document.referrer || undefined,
        ...utm,
      }).catch(() => { /* silent */ });
    } catch { /* silent */ }
  }, []);

  // NYC School autocomplete
  const searchSchools = useCallback((query) => {
    if (query.length < 2) { setSchoolSuggestions([]); return; }
    setSchoolSearching(true);
    const isDBN = /^\d{2}[A-Za-z]\d{0,3}$/.test(query.trim());
    const params = isDBN
      ? `$where=upper(system_code) like '%25${query.trim().toUpperCase()}%25'&$limit=8&$select=system_code,location_name,primary_address_line_1,state_code,geographical_district_code,principal_phone_number`
      : `$where=upper(location_name) like '%25${query.trim().toUpperCase().replace(/'/g, "''")}%25'&$limit=8&$select=system_code,location_name,primary_address_line_1,state_code,geographical_district_code,principal_phone_number`;
    fetch(`${NYC_SCHOOLS_API}?${params}`)
      .then(r => r.json())
      .then(data => {
        setSchoolSuggestions(Array.isArray(data) ? data : []);
        setShowSuggestions(true);
      })
      .catch(() => setSchoolSuggestions([]))
      .finally(() => setSchoolSearching(false));
  }, []);

  const handleSchoolQueryChange = (val) => {
    setSchoolQuery(val);
    handleContactChange('school_name', val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchSchools(val), 300);
  };

  // NYC School lookup by DBN — auto-fills name + address when a valid DBN is typed
  // into the DBN field directly (e.g. 30Q149).
  const dbnDebounceRef = useRef(null);
  const lookupByDbn = useCallback((dbn) => {
    const clean = (dbn || '').trim().toUpperCase();
    if (!/^\d{2}[A-Z]\d{3}$/.test(clean)) return;
    const params = `$where=upper(system_code)='${clean}'&$limit=1&$select=system_code,location_name,primary_address_line_1,state_code,principal_phone_number,principal_name`;
    fetch(`${NYC_SCHOOLS_API}?${params}`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const s = data[0];
          const name = s.location_name || '';
          const addr = s.primary_address_line_1 || '';
          const state = s.state_code || 'NY';
          const fullAddr = addr ? `${addr}, New York, ${state}` : '';
          setSchoolQuery(name);
          setSchoolSuggestions([]);
          setShowSuggestions(false);
          setContact(prev => ({
            ...prev,
            school_name: name,
            school_dbn: s.system_code || clean,
            school_address: fullAddr,
            contact_phone: prev.contact_phone || (s.principal_phone_number || '').toString().trim(),
          }));
        }
      })
      .catch(() => { /* silent */ });
  }, []);

  const handleDbnChange = (val) => {
    handleContactChange('school_dbn', val);
    clearTimeout(dbnDebounceRef.current);
    dbnDebounceRef.current = setTimeout(() => lookupByDbn(val), 350);
  };

  const selectSchool = (school) => {
    const name = school.location_name || '';
    const dbn = school.system_code || '';
    const addr = school.primary_address_line_1 || '';
    const state = school.state_code || 'NY';
    const fullAddr = addr ? `${addr}, New York, ${state}` : '';
    setSchoolQuery(name);
    setContact(prev => ({ ...prev, school_name: name, school_dbn: dbn, school_address: fullAddr, contact_phone: prev.contact_phone || (school.principal_phone_number || '').toString().trim() }));
    setShowSuggestions(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => { if (schoolRef.current && !schoolRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const grouped = {};
  products.forEach(p => {
    if (!grouped[p.category]) grouped[p.category] = [];
    grouped[p.category].push(p);
  });

  const cartItems = Object.entries(cart).filter(([, q]) => q > 0);
  const cartProducts = cartItems.map(([sku, qty]) => {
    const p = products.find(x => x.sku === sku);
    return { ...p, quantity: qty, line_total: (p?.price || 0) * qty };
  });
  const subtotal = cartProducts.reduce((s, p) => s + p.line_total, 0);
  const hasDigital = cartProducts.some(p => p.category === 'digital' || p.category === 'complete');

  const updateQty = (sku, delta) => {
    setCart(prev => {
      const p = products.find(x => x.sku === sku);
      const min = p?.min_qty || 1;
      const curr = prev[sku] || 0;
      const next = curr + delta;
      if (next < 0) return prev;
      if (next === 0) { const { [sku]: _, ...rest } = prev; return rest; }
      return { ...prev, [sku]: next };
    });
  };

  const setQty = (sku, val) => {
    const num = parseInt(val) || 0;
    if (num <= 0) {
      setCart(prev => { const { [sku]: _, ...rest } = prev; return rest; });
    } else {
      setCart(prev => ({ ...prev, [sku]: num }));
    }
  };

  const handleContactChange = (field, value) => {
    setContact(prev => ({ ...prev, [field]: value }));
  };

  const validateContact = () => {
    const required = ['school_name', 'school_dbn', 'school_address', 'contact_name', 'contact_email', 'contact_phone'];
    for (const f of required) {
      if (!contact[f]?.trim()) return false;
    }
    if (hasDigital) {
      if (!contact.it_admin_name?.trim() || !contact.it_admin_email?.trim()) return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const items = cartItems.map(([sku, qty]) => ({ sku, quantity: qty }));
      // Safety: if the user picked email/both but then cleared the purchasing
      // email on step 2, fall back to download so the server behaviour matches
      // what the user actually sees on the success screen.
      const effectiveDelivery = (deliveryMethod !== 'download' && !contact.purchasing_admin_email?.trim())
        ? 'download'
        : deliveryMethod;
      const res = await axios.post(`${API}/api/quotes/generate`, {
        contact, items, notes, effective_date: effectiveDate,
        delivery_method: effectiveDelivery,
      });
      setResult(res.data);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate quote. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadQuote = () => {
    if (result?.pdf_url) {
      const a = document.createElement('a');
      a.href = `${API}${result.pdf_url}`;
      a.download = `MOSAIC_Quote_${result.quote_number}.pdf`;
      a.click();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-[#002FA7] text-white py-10 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
              <FileText size={18} />
            </div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>MOSAIC Quote Generator</h1>
          </div>
          <p className="text-blue-200 text-sm">Build your custom quote for language-proficiency assessment materials — print, digital, or both.</p>

          {/* Stepper */}
          <div className="flex items-center gap-2 mt-6">
            {[
              { n: 1, label: 'Select Products' },
              { n: 2, label: 'School Info' },
              { n: 3, label: 'Review & Submit' },
            ].map(({ n, label }) => (
              <div key={n} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                  step >= n ? 'bg-amber-400 border-amber-400 text-[#002FA7]' : 'border-white/30 text-white/50'
                }`}>
                  {step > n ? <Check size={14} /> : n}
                </div>
                <span className={`text-xs font-medium ${step >= n ? 'text-white' : 'text-white/40'}`}>{label}</span>
                {n < 3 && <ChevronRight size={14} className="text-white/20 mx-1" />}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6" data-testid="quote-generator">

        {/* STEP 1: Product Selection */}
        {step === 1 && (
          <div className="space-y-6">
            {Object.keys(CATEGORY_META).map(cat => {
              const meta = CATEGORY_META[cat];
              const prods = grouped[cat] || [];
              if (prods.length === 0) return null;
              const Icon = meta.icon;
              return (
                <div key={cat} className={`border rounded-lg overflow-hidden ${meta.color}`}>
                  <div className="px-4 py-3 border-b border-gray-200 bg-white/60">
                    <div className="flex items-center gap-2">
                      <Icon size={16} className="text-gray-600" />
                      <h2 className="text-sm font-bold text-gray-800">{meta.label}</h2>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{meta.desc}</p>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {prods.map(p => {
                      const qty = cart[p.sku] || 0;
                      return (
                        <div key={p.sku} className="flex items-center justify-between px-4 py-3 bg-white/80 hover:bg-white transition-colors" data-testid={`product-${p.sku}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${meta.badge}`}>{p.sku}</span>
                              <span className="text-sm font-medium text-gray-800 truncate">{p.name}</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              {p.price > 0 ? `$${p.price.toFixed(2)} / ${p.unit}` : `Per ${p.unit}`}
                              {' | '}Min: {p.min_qty}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => updateQty(p.sku, -1)} className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-30" disabled={qty === 0} data-testid={`qty-minus-${p.sku}`}>
                              <Minus size={12} />
                            </button>
                            <input
                              type="number"
                              value={qty || ''}
                              onChange={e => setQty(p.sku, e.target.value)}
                              placeholder="0"
                              className="w-12 text-center text-sm font-bold border border-gray-200 rounded px-1 py-1"
                              min={0}
                              data-testid={`qty-input-${p.sku}`}
                            />
                            <button onClick={() => updateQty(p.sku, 1)} className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100" data-testid={`qty-plus-${p.sku}`}>
                              <Plus size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Cart Summary */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 sticky bottom-4 shadow-lg" data-testid="cart-summary">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingCart size={16} className="text-[#002FA7]" />
                  <span className="text-sm font-bold text-gray-800">{cartItems.length} item{cartItems.length !== 1 ? 's' : ''} selected</span>
                  {subtotal > 0 && <span className="text-sm font-bold text-[#002FA7]">${subtotal.toFixed(2)}</span>}
                </div>
                <Button onClick={() => setStep(2)} disabled={cartItems.length === 0} className="bg-[#002FA7] hover:bg-[#001d6e]" data-testid="continue-to-contact">
                  Continue <ChevronRight size={14} />
                </Button>
              </div>
              {cartItems.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {cartProducts.map(p => (
                    <span key={p.sku} className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                      {p.name} x{p.quantity}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-2 flex items-center gap-1.5 text-[10px] text-gray-400">
                <ScrollText size={10} />
                <span>NYC Sole Vendor Letter available after quote generation — no competitive bid needed.</span>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Contact Information */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h2 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Building2 size={16} className="text-[#002FA7]" /> School Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative" ref={schoolRef}>
                  <label className="text-xs font-medium text-gray-600">School Name *</label>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <Input
                      value={schoolQuery || contact.school_name}
                      onChange={e => handleSchoolQueryChange(e.target.value)}
                      onFocus={() => { if (schoolSuggestions.length) setShowSuggestions(true); }}
                      placeholder="Start typing school name or DBN..."
                      className="pl-9"
                      data-testid="input-school-name"
                    />
                    {schoolSearching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 animate-spin" />}
                  </div>
                  {showSuggestions && schoolSuggestions.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="school-suggestions">
                      {schoolSuggestions.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => selectSchool(s)}
                          className="w-full text-left px-3 py-2.5 hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors"
                          data-testid={`school-suggestion-${i}`}
                        >
                          <div className="text-sm font-medium text-gray-800">{s.location_name}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                            <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-bold">{s.system_code}</span>
                            <span>{s.primary_address_line_1}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {showSuggestions && schoolQuery.length >= 2 && !schoolSearching && schoolSuggestions.length === 0 && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-3 text-xs text-gray-500" data-testid="school-no-results">
                      No NYC schools found matching "{schoolQuery}". You can type the name manually.
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">School DBN *</label>
                  <Input value={contact.school_dbn} onChange={e => handleDbnChange(e.target.value)} placeholder="e.g., 02M124" data-testid="input-school-dbn" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-600">School Address *</label>
                  <Input value={contact.school_address} onChange={e => handleContactChange('school_address', e.target.value)} placeholder="e.g., 40 Division Street, New York, NY 10002" data-testid="input-school-address" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h2 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                <User size={16} className="text-emerald-600" /> Requesting Contact *
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600">Full Name *</label>
                  <Input value={contact.contact_name} onChange={e => handleContactChange('contact_name', e.target.value)} placeholder="John Doe" data-testid="input-contact-name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Email *</label>
                  <Input type="email" value={contact.contact_email} onChange={e => handleContactChange('contact_email', e.target.value)} placeholder="jdoe@schools.nyc.gov" data-testid="input-contact-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Phone *</label>
                  <Input type="tel" value={contact.contact_phone} onChange={e => handleContactChange('contact_phone', e.target.value)} placeholder="(212) 555-0124" data-testid="input-contact-phone" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Quote Date</label>
                  <Input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} data-testid="input-effective-date" />
                  <p className="text-[10px] text-gray-400 mt-0.5">Defaults to today; editable here and on revision.</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h2 className="text-sm font-bold text-gray-800 mb-1 flex items-center gap-2">
                <Mail size={16} className="text-amber-600" /> IT & Purchasing Contacts {hasDigital ? <span className="text-red-500">*</span> : '(Optional)'}
              </h2>
              {hasDigital && (
                <p className="text-xs text-amber-600 mb-4 flex items-center gap-1">
                  <AlertCircle size={12} /> IT Admin contact is required for digital license quotes.
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600">IT Admin Name {hasDigital && <span className="text-red-500">*</span>}</label>
                  <Input value={contact.it_admin_name} onChange={e => handleContactChange('it_admin_name', e.target.value)} placeholder="Jane Doe" data-testid="input-it-name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">IT Admin Email {hasDigital && <span className="text-red-500">*</span>}</label>
                  <Input type="email" value={contact.it_admin_email} onChange={e => handleContactChange('it_admin_email', e.target.value)} placeholder="janedoe@schools.nyc.gov" data-testid="input-it-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Purchasing Admin Name</label>
                  <Input value={contact.purchasing_admin_name} onChange={e => handleContactChange('purchasing_admin_name', e.target.value)} placeholder="Jane Doe" data-testid="input-purchasing-name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Purchasing Admin Email</label>
                  <Input type="email" value={contact.purchasing_admin_email} onChange={e => handleContactChange('purchasing_admin_email', e.target.value)} placeholder="janedoe@schools.nyc.gov" data-testid="input-purchasing-email" />
                </div>
              </div>
              <p className="text-[10px] text-gray-400 mt-2">Optional. You can choose to email, download, or both on the Review step.</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <label className="text-xs font-medium text-gray-600">Additional Notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g., Need materials before October 2026. Requesting sole source documentation."
                className="w-full mt-1 border border-gray-200 rounded-md p-2 text-sm resize-none h-20"
                data-testid="input-notes"
              />
            </div>

            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="back-to-products">Back to Products</Button>
              <Button onClick={() => setStep(3)} disabled={!validateContact()} className="bg-[#002FA7] hover:bg-[#001d6e]" data-testid="continue-to-review">
                Review Quote <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3: Review & Submit */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h2 className="text-sm font-bold text-gray-800 mb-3">Quote Summary</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                <div className="bg-gray-50 rounded p-3">
                  <p className="font-bold text-gray-800">{contact.school_name}</p>
                  <p className="text-gray-500">DBN: {contact.school_dbn}</p>
                  <p className="text-gray-500">{contact.school_address}</p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <p className="font-bold text-gray-800">{contact.contact_name}</p>
                  <p className="text-gray-500">{contact.contact_email}</p>
                  <p className="text-gray-500">{contact.contact_phone}</p>
                </div>
              </div>

              {/* Line items */}
              <table className="w-full text-xs" data-testid="review-line-items">
                <thead>
                  <tr className="bg-[#002FA7] text-white">
                    <th className="text-left px-3 py-2 font-semibold">Item #</th>
                    <th className="text-left px-3 py-2 font-semibold">Product</th>
                    <th className="text-center px-3 py-2 font-semibold">Qty</th>
                    <th className="text-right px-3 py-2 font-semibold">Unit Price</th>
                    <th className="text-right px-3 py-2 font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {cartProducts.map((p, i) => (
                    <tr key={p.sku} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 font-bold text-[#002FA7]">{p.sku}</td>
                      <td className="px-3 py-2 text-gray-700">{p.name}</td>
                      <td className="px-3 py-2 text-center">{p.quantity}</td>
                      <td className="px-3 py-2 text-right">{p.price > 0 ? `$${p.price.toFixed(2)}` : 'Contact'}</td>
                      <td className="px-3 py-2 text-right font-bold">{p.line_total > 0 ? `$${p.line_total.toFixed(2)}` : 'Contact'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[#002FA7]">
                    <td colSpan={4} className="px-3 py-2 text-right font-bold text-gray-800">Subtotal:</td>
                    <td className="px-3 py-2 text-right font-bold text-[#002FA7] text-sm">
                      {subtotal > 0 ? `$${subtotal.toFixed(2)}` : 'Contact for pricing'}
                    </td>
                  </tr>
                </tfoot>
              </table>

              {notes && (
                <div className="mt-3 bg-amber-50 rounded p-2 text-xs text-amber-800">
                  <strong>Notes:</strong> {notes}
                </div>
              )}
            </div>

            {/* v6.33 — Delivery method selector (customer-controlled). */}
            <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="delivery-method-card">
              <h2 className="text-sm font-bold text-gray-800 mb-1">How should we deliver the quote?</h2>
              <p className="text-[11px] text-gray-500 mb-3">Default is <strong>Download only</strong>. The quote is never auto-emailed unless you ask.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {[
                  { v: 'download', title: 'Download only',      sub: 'PDF link on the next screen; nothing is emailed.',                      icon: Download },
                  { v: 'email',    title: 'Email it to us',      sub: 'Sent to the Purchasing Admin email on the previous step.',             icon: Mail     },
                  { v: 'both',     title: 'Email + Download',    sub: 'Both: email the Purchasing Admin and give me the download link.',      icon: Send     },
                ].map(opt => {
                  const Icon = opt.icon;
                  const active = deliveryMethod === opt.v;
                  const disabled = (opt.v !== 'download') && !contact.purchasing_admin_email?.trim();
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => !disabled && setDeliveryMethod(opt.v)}
                      disabled={disabled}
                      className={`text-left border rounded-md p-3 transition ${
                        active ? 'border-[#002FA7] bg-[#002FA7]/5 ring-1 ring-[#002FA7]'
                               : 'border-gray-200 hover:border-gray-300 bg-white'
                      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      data-testid={`delivery-method-${opt.v}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon size={14} className={active ? 'text-[#002FA7]' : 'text-gray-500'} />
                        <span className={`text-sm font-semibold ${active ? 'text-[#002FA7]' : 'text-gray-800'}`}>{opt.title}</span>
                        {active && <Check size={14} className="ml-auto text-[#002FA7]" />}
                      </div>
                      <p className="text-[11px] text-gray-500">{opt.sub}</p>
                      {disabled && (
                        <p className="text-[10px] text-amber-600 mt-1">Enter a Purchasing Admin Email on the previous step to enable this option.</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-700" data-testid="quote-error">
                <AlertCircle size={16} /> {error}
              </div>
            )}

            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="back-to-contact">Back to Contact Info</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="bg-[#002FA7] hover:bg-[#001d6e] gap-2" data-testid="submit-quote">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {submitting ? 'Generating...' : 'Generate Quote'}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4: Success */}
        {step === 4 && result && (
          <div className="text-center space-y-6 py-8" data-testid="quote-success">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <Check size={32} className="text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Quote Generated!</h2>
              <div className="mt-3 inline-block bg-[#002FA7]/5 border-2 border-[#002FA7]/20 rounded-lg px-6 py-3" data-testid="quote-number-display">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-0.5">Quote Number</p>
                <p className="text-lg font-black text-[#002FA7] tracking-wide font-mono">{result.quote_number}</p>
              </div>
            </div>

            {result.email_sent && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 inline-flex items-center gap-2 text-sm text-emerald-700" data-testid="quote-emailed-banner">
                <Mail size={16} /> Quote emailed to {result.email_to}
              </div>
            )}
            {result.delivery_method === 'download' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 inline-flex items-center gap-2 text-sm text-blue-700" data-testid="quote-download-only-banner">
                <Download size={16} /> Download-only delivery — no email was sent.
              </div>
            )}

            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Button onClick={downloadQuote} className="bg-[#002FA7] hover:bg-[#001d6e] gap-2" data-testid="download-quote-pdf">
                <Download size={14} /> Download Quote PDF
              </Button>
              {/* v6.33 — Only the new Teacher Edition Sole Vendor Letter is
                  exposed. The legacy Print Materials / USB / SSES letters
                  are retired until each has been rewritten to the new
                  Teacher Edition vocabulary and FFU framework. */}
              <a
                href="/sales/MOSAIC_TeacherEdition_SoleVendor_Letter.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-sole-vendor-letter-teacher-edition"
              >
                <Button variant="outline" className="border-emerald-600/40 text-emerald-700 hover:bg-emerald-50 gap-2">
                  <ScrollText size={14} /> Teacher Edition Sole Vendor Letter
                </Button>
              </a>
              <Button variant="outline" onClick={() => { setStep(1); setCart({}); setResult(null); setContact({
                school_name: '', school_dbn: '', school_address: '',
                contact_name: '', contact_email: '', contact_phone: '',
                it_admin_name: '', it_admin_email: '',
                purchasing_admin_name: '', purchasing_admin_email: '',
              }); setNotes(''); setEffectiveDate(new Date().toISOString().slice(0, 10)); }} data-testid="new-quote-btn">
                Create Another Quote
              </Button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 max-w-lg mx-auto" data-testid="procurement-info-box">
              <p className="text-xs text-blue-800 text-center">
                <strong>NYC Procurement:</strong> Download the Sole Vendor Letter above to attach with your Purchase Order.
                Print-ready English language proficiency assessment materials for schools.
              </p>
            </div>

            <p className="text-xs text-gray-400 max-w-md mx-auto">
              This quote is valid for 30 days. To place an order, email a signed copy or your school's Purchase Order to
              <strong> orders@mosaicassessmentco.com</strong> or call <strong>(973) 303-3414</strong>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteGenerator;
